import { createStore } from 'solid-js/store';
import { START_FEN, type Move, type Square } from '@domain/chess/types';
import { flags } from '@config/flags';

/**
 * Single source of truth for the game session.
 * Mutate only via actions.ts — components read store, never write fields directly.
 */
export interface GameState {
  startFen: string;
  fen: string;
  history: Move[];
  /** Position = startFen + history[0..cursor). */
  cursor: number;
  orientation: 'white' | 'black';
  selected: Square | null;
  legalTargets: Square[];
  pendingPromotion: { from: Square; to: Square } | null;
  lastMove: { from: Square; to: Square } | null;
  /** Catalog opening last loaded (search / quiz Show). */
  loadedOpeningName: string | null;
  /** Squares occupied correctly by moved pieces immediately before reveal. */
  revealCorrectSquares: Square[];
  /** Click-to-mark puzzles (Horse Ride) instead of moving pieces. */
  interaction: 'play' | 'mark';
  puzzlePieces: { square: Square; color: 'w' | 'b'; role: 'k' | 'q' | 'r' | 'b' | 'n' | 'p' }[];
  markedSquares: Square[];
  markLog: Square[];
  revealMarks: { sq: Square; mark: 'correct' | 'wrong' | 'missing' }[];
  engine: {
    status: 'idle' | 'loading' | 'ready' | 'thinking' | 'error';
    errorMessage?: string;
    currentRequestId: string | null;
    stickyAnalyze: boolean;
    depth: number;
    score?: { cp?: number; mate?: number };
    /** Current engine best move in UCI (temporary display). */
    bestMoveUci?: string;
    loadProgress?: number;
  };
  ui: {
    creatorMode: boolean;
    valuationMode: 'tint' | 'morph' | 'off';
    showEvalNumber: boolean;
  };
}

function initialState(): GameState {
  return {
    startFen: START_FEN,
    fen: START_FEN,
    history: [],
    cursor: 0,
    orientation: 'white',
    selected: null,
    legalTargets: [],
    pendingPromotion: null,
    lastMove: null,
    loadedOpeningName: null,
    revealCorrectSquares: [],
    interaction: 'play',
    puzzlePieces: [],
    markedSquares: [],
    markLog: [],
    revealMarks: [],
    engine: {
      status: 'idle',
      currentRequestId: null,
      stickyAnalyze: false,
      depth: 16,
    },
    ui: {
      creatorMode: flags.creatorModeDefault,
      valuationMode: 'tint',
      showEvalNumber: flags.showEvalBadge,
    },
  };
}

export const [gameStore, setGameStore] = createStore<GameState>(initialState());

/** Reset store (tests / new game). Prefer actions for product flows. */
export function resetGameStore(): void {
  setGameStore(initialState());
}
